import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BuildIcon from '@mui/icons-material/Build';
import ExposureIcon from '@mui/icons-material/Exposure';
import {
  ROUTE_CASES,
  ROUTE_TREATMENTS,
  ROUTE_DRUGS,
  ROUTE_CALCULATOR,
} from '../../config/routes.config';
import DiagnosticsIcon from '../../assets/icons/DiagnosticsIcon';
import DiseasesIcon from '../../assets/icons/DiseasesIcon';
import DrugsIcon from '../../assets/icons/DrugsIcon';

const useStyles = makeStyles()((theme) => ({
  navContainer: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(2),
    gap: theme.spacing(1),
  },
  navButton: {
    color: '#444444',
    textTransform: 'none',
    fontSize: '16px',
    fontWeight: 500,
    padding: theme.spacing(1, 2),
    border: '1px solid rgba(0, 0, 0, 0.15)',
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)',
      borderColor: 'rgba(0, 0, 0, 0.25)',
    },
    '& .MuiSvgIcon-root': {
      marginRight: theme.spacing(1),
      color: '#444444',
    },
  },
  navButtonActive: {
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    borderColor: 'rgba(0, 0, 0, 0.3)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.15)',
    },
  },
  menuItem: {
    minWidth: 200,
  },
  menuIcon: {
    minWidth: 36,
    color: '#444444',
  },
}));

export const MainNavigation = () => {
  const { classes, cx } = useStyles();
  const location = useLocation();
  const [toolsAnchorEl, setToolsAnchorEl] = useState<null | HTMLElement>(null);

  const isToolsOpen = Boolean(toolsAnchorEl);
  const isCasesActive = location.pathname.startsWith('/cases');
  const isToolsActive = location.pathname.startsWith('/tools');

  const handleToolsClick = (event: React.MouseEvent<HTMLElement>) => {
    setToolsAnchorEl(event.currentTarget);
  };

  const handleToolsClose = () => {
    setToolsAnchorEl(null);
  };

  const toolsMenuItems = [
    { title: 'Diseases', to: ROUTE_TREATMENTS, icon: <DiseasesIcon /> },
    { title: 'Drug Substances', to: ROUTE_DRUGS, icon: <DrugsIcon /> },
    {
      title: 'Drug Dosage Calculator',
      to: ROUTE_CALCULATOR,
      icon: <ExposureIcon />,
    },
  ];

  return (
    <Box className={classes.navContainer}>
      <Button
        component={Link}
        to={ROUTE_CASES}
        className={cx(
          classes.navButton,
          isCasesActive && classes.navButtonActive
        )}
        startIcon={<DiagnosticsIcon />}
      >
        Diagnostics
      </Button>

      <Button
        className={cx(
          classes.navButton,
          isToolsActive && classes.navButtonActive
        )}
        onClick={handleToolsClick}
        startIcon={<BuildIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        aria-controls={isToolsOpen ? 'tools-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isToolsOpen ? 'true' : undefined}
      >
        Tools
      </Button>
      <Menu
        id="tools-menu"
        anchorEl={toolsAnchorEl}
        open={isToolsOpen}
        onClose={handleToolsClose}
        MenuListProps={{
          'aria-labelledby': 'tools-button',
        }}
      >
        {toolsMenuItems.map((item) => (
          <MenuItem
            key={item.to}
            component={Link}
            to={item.to}
            onClick={handleToolsClose}
            className={classes.menuItem}
          >
            <ListItemIcon className={classes.menuIcon}>
              {item.icon}
            </ListItemIcon>
            <ListItemText>{item.title}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default MainNavigation;
